import { Button } from '@respectly/ui';
import { Pencil, PowerOff, Trash2, Percent, DollarSign, Infinity as InfinityIcon } from 'lucide-react';
import { format } from 'date-fns';
import type { DiscountWithStats } from '@respectly/shared-types';
import { DiscountStatusBadge, DiscountScopeBadge, DiscountCodeBadge } from './DiscountStatusBadge';

interface DiscountTableProps {
  discounts: DiscountWithStats[];
  onEdit: (discount: DiscountWithStats) => void;
  onDeactivate: (id: string) => void;
  onDelete: (id: string) => void;
}

function formatValue(discount: DiscountWithStats) {
  if (discount.type === 'percentage') {
    return (
      <span className="flex items-center gap-1 font-semibold text-slate-800 dark:text-slate-200">
        <Percent className="h-3.5 w-3.5 text-violet-500" />
        {discount.value}%
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 font-semibold text-slate-800 dark:text-slate-200">
      <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
      {discount.value.toFixed(2)}
    </span>
  );
}

function formatValidity(discount: DiscountWithStats) {
  if (!discount.startsAt && !discount.expiresAt) {
    return <span className="text-slate-400 text-xs">No expiry</span>;
  }
  const parts: string[] = [];
  if (discount.startsAt) parts.push(`From ${format(new Date(discount.startsAt), 'MMM d, yyyy')}`);
  if (discount.expiresAt) parts.push(`Until ${format(new Date(discount.expiresAt), 'MMM d, yyyy')}`);
  return <span className="text-xs text-slate-600 dark:text-slate-400">{parts.join(' · ')}</span>;
}

function formatUsage(discount: DiscountWithStats) {
  if (discount.maxUses === null || discount.maxUses === undefined) {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-500">
        {discount.usedCount} / <InfinityIcon className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span className="text-xs text-slate-500">
      {discount.usedCount} / {discount.maxUses}
    </span>
  );
}

export function DiscountTable({
  discounts,
  onEdit,
  onDeactivate,
  onDelete,
}: DiscountTableProps) {
  if (discounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-slate-500 text-sm">No discounts yet.</p>
        <p className="text-slate-400 text-xs mt-1">
          Click &quot;New Discount&quot; to create your first discount or promo code.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Name / Code
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Value
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Scope
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Assignments
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Usage
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Valid
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
              Status
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {discounts.map((discount) => (
            <tr
              key={discount.id}
              className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
            >
              {/* Name / Code */}
              <td className="px-4 py-3">
                <div className="space-y-1">
                  <p className="font-medium text-slate-800 dark:text-slate-200 leading-tight">
                    {discount.name}
                  </p>
                  {discount.code && <DiscountCodeBadge code={discount.code} />}
                  {discount.scope === 'account' && discount.tenantName && (
                    <p className="text-xs text-slate-400">{discount.tenantName}</p>
                  )}
                </div>
              </td>

              {/* Value */}
              <td className="px-4 py-3">{formatValue(discount)}</td>

              {/* Scope */}
              <td className="px-4 py-3">
                <DiscountScopeBadge scope={discount.scope} />
                {discount.scope === 'specific_plans' &&
                  discount.planIds &&
                  discount.planIds.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      {discount.planIds.length} plan{discount.planIds.length !== 1 ? 's' : ''}
                    </p>
                  )}
              </td>

              {/* Assignments */}
              <td className="px-4 py-3">
                <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-medium">
                  {discount.assignmentCount}
                </span>
              </td>

              {/* Usage */}
              <td className="px-4 py-3">{formatUsage(discount)}</td>

              {/* Valid */}
              <td className="px-4 py-3">{formatValidity(discount)}</td>

              {/* Status */}
              <td className="px-4 py-3">
                <DiscountStatusBadge discount={discount} />
              </td>

              {/* Actions */}
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(discount)}
                    className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>

                  {discount.usedCount === 0 && discount.assignmentCount === 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(discount.id)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeactivate(discount.id)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-amber-500"
                      title={discount.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <PowerOff className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
